import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { canManageFeeStructures } from "./feeStructureUtils";
import FinanceNav from "@/components/finance/FinanceNav";

const NGN = (minor) => `₦${((Number(minor) || 0) / 100).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})}`;

const ENTRY_LABELS = {
  OBLIGATION_CHARGED: "Charge", OBLIGATION_REVERSED: "Charge cancelled",
  SCHOLARSHIP_APPLIED: "Scholarship applied",
  SCHOLARSHIP_REVERSED: "Scholarship reversed",
  PAYMENT_RECEIVED: "Payment received",
  PAYMENT_ALLOCATED: "Payment allocated",
  PAYMENT_ALLOCATION_REVERSED: "Allocation reversed",
  PAYMENT_REFUNDED: "Payment refunded",
};

const Fees = () => {
  const { user } = useAuth();
  const canManageStructures = canManageFeeStructures(user);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState("");
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState("");
  const [studentId, setStudentId] = useState("");
  const [enrollmentId, setEnrollmentId] = useState("");
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementError, setStatementError] = useState("");
  const [detail, setDetail] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    const loadStudents = async () => {
      setStudentsLoading(true);
      try {
        const { data } = await api.get("/finance/students");
        if (!active) return;
        setStudents(data);
        setStudentsError("");
        setStudentId((current) => current || data.find((student) => student.programs?.length)?.id || "");
      } catch (error) {
        if (!active) return;
        const message = formatApiError(error);
        setStudentsError(message);
        toast.error(`Student directory: ${message}`);
      } finally {
        if (active) setStudentsLoading(false);
      }
    };
    loadStudents();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const loadContext = async () => {
      setContextLoading(true);
      try {
        const { data } = await api.get("/finance/context");
        if (!active) return;
        setContext(data);
        setContextError("");
      } catch (error) {
        if (!active) return;
        const message = formatApiError(error);
        setContextError(message);
        toast.error(`Finance period: ${message}`);
      } finally {
        if (active) setContextLoading(false);
      }
    };
    loadContext();
    return () => { active = false; };
  }, []);

  const selectedStudent = useMemo(
    () => students.find((student) => student.id === studentId),
    [studentId, students],
  );
  const placements = useMemo(() => {
    const all = selectedStudent?.programs || [];
    if (!context?.session?.id) return all;
    return all.filter((placement) => placement.session_id === context.session.id);
  }, [context, selectedStudent]);

  useEffect(() => {
    setEnrollmentId((current) => placements.some((placement) => placement.enrollment_id === current)
      ? current
      : placements[0]?.enrollment_id || "");
  }, [placements]);

  const selectedPlacement = useMemo(
    () => placements.find((placement) => placement.enrollment_id === enrollmentId),
    [enrollmentId, placements],
  );

  useEffect(() => {
    let active = true;
    if (!selectedStudent || !selectedPlacement || !context?.session?.id || !context?.term?.id) {
      setStatement(null);
      setStatementError("");
      return () => { active = false; };
    }
    const loadStatement = async () => {
      setStatementLoading(true);
      setStatement(null);
      setStatementError("");
      try {
        const { data } = await api.get(`/finance/statements/${selectedStudent.id}`, {
          params: {
            enrollment_id: selectedPlacement.enrollment_id,
            program_id: selectedPlacement.program_id,
            session_id: context.session.id,
            term_id: context.term.id,
          },
        });
        if (active) setStatement(data);
      } catch (error) {
        if (!active) return;
        const message = formatApiError(error);
        setStatementError(message);
        toast.error(`Finance statement: ${message}`);
      } finally {
        if (active) setStatementLoading(false);
      }
    };
    loadStatement();
    return () => { active = false; };
  }, [context, selectedPlacement, selectedStudent, refreshKey]);

  useEffect(() => {
    if (!selectedStudent || !selectedPlacement || !context) { setDetail(null); return; }
    api.get(`/finance/placements/${selectedStudent.id}/detail`, { params: { enrollment_id: selectedPlacement.enrollment_id, program_id: selectedPlacement.program_id, session_id: context.session.id, term_id: context.term.id } })
      .then(({ data }) => setDetail(data)).catch(() => setDetail(null));
  }, [context, selectedPlacement, selectedStudent, refreshKey]);

  const periodLabel = context
    ? `${context.session.name} · ${context.term.name || context.term.code}`
    : "Current finance period";

  return (
    <div data-testid="fees-page">
      <FinanceNav />
      <PageHeader
        overline="Finance"
        title="Fees & POS Payments"
        description="Review canonical placement balances, payment history and record authorized manual payments."
        actions={canManageStructures && <Button asChild variant="outline"><Link to="/app/fees/structures">Fee Structure</Link></Button>}
      />

      <Card className="p-5 border-[#E2E8F0] mb-6">
        <div className="grid gap-4 md:grid-cols-[minmax(260px,1fr)_2fr] md:items-end">
          <div className="space-y-1.5">
            <Label className="text-[#1B2C5A] text-sm">Student</Label>
            <Select value={studentId} onValueChange={setStudentId} disabled={studentsLoading || !students.length}>
              <SelectTrigger data-testid="finance-student-select">
                <SelectValue placeholder={studentsLoading ? "Loading students…" : "Choose student"} />
              </SelectTrigger>
              <SelectContent>
                {students.filter((student) => student.programs?.length).map((student) => (
                  <SelectItem key={student.id} value={student.id}>
                    {student.full_name} · {student.admission_no || "No admission number"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="overline text-[#9DCD3F]">Finance Period</div>
            <div className="text-sm font-medium text-[#1B2C5A] mt-1" data-testid="finance-period">
              {contextLoading ? "Loading…" : contextError || periodLabel}
            </div>
          </div>
        </div>
        {studentsError && <InlineError className="mt-4" message={`Student directory unavailable: ${studentsError}`} />}
      </Card>

      {selectedStudent && placements.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="finance-placement-switcher">
          {placements.map((placement) => (
            <Button
              key={placement.enrollment_id}
              type="button"
              variant={placement.enrollment_id === enrollmentId ? "default" : "outline"}
              className={placement.enrollment_id === enrollmentId
                ? "bg-[#1B2C5A] hover:bg-[#14224A] text-white"
                : "border-[#E2E8F0] text-[#1B2C5A]"}
              onClick={() => setEnrollmentId(placement.enrollment_id)}
              data-testid={`finance-placement-${placement.program_code || placement.program_id}`}
            >
              {placement.program_name || placement.program_code || "Program"}
              {placement.class_name ? ` · ${placement.class_name}` : ""}
            </Button>
          ))}
        </div>
      )}

      {(studentsLoading || contextLoading || statementLoading) && (
        <Card className="p-12 border-[#E2E8F0] text-[#475569] flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading finance statement…
        </Card>
      )}
      {!studentsLoading && !contextLoading && !statementLoading && !selectedStudent && (
        <EmptyState message="Choose a student to view placement-specific finance data." />
      )}
      {!studentsLoading && !contextLoading && !statementLoading && selectedStudent && !placements.length && (
        <EmptyState message="This student has no active placement in the current finance period." />
      )}
      {statementError && !statementLoading && <InlineError message={statementError} />}
      {statement && !statementLoading && <><ManualPayment detail={detail} student={selectedStudent} placement={selectedPlacement} context={context} onRecorded={() => setRefreshKey((x) => x + 1)} /><StatementView statement={statement} placement={selectedPlacement} /></>}
    </div>
  );
};

const ManualPayment = ({ detail, student, placement, context, onRecorded }) => {
  const [open, setOpen] = useState(false); const [selected, setSelected] = useState([]); const [amount, setAmount] = useState(""); const [method, setMethod] = useState("bank"); const [reference, setReference] = useState(""); const [date, setDate] = useState(new Date().toISOString().slice(0, 10)); const [note, setNote] = useState(""); const [saving, setSaving] = useState(false);
  const invoices = (detail?.invoices || []).filter((x) => Number(x.net_balance_minor) > 0);
  const toggle = (id) => setSelected((x) => x.includes(id) ? x.filter((v) => v !== id) : [...x, id]);
  const submit = async () => { const received = Math.round(Number(amount) * 100); if (!received || !selected.length) return toast.error("Select invoices and enter the amount received"); setSaving(true); try {
    const paymentKey = `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const { data: payment } = await api.post("/finance/payments", { student_id: student.id, enrollment_id: placement.enrollment_id, program_id: placement.program_id, session_id: context.session.id, term_id: context.term.id, amount_minor: received, currency: "NGN", payment_method: method, idempotency_key: paymentKey, provider_reference: reference || undefined, metadata: { note, receipt_reference: reference || undefined }, received_at: date ? new Date(`${date}T12:00:00`).toISOString() : undefined });
    let remaining = received;
    for (const invoice of invoices.filter((x) => selected.includes(x.id))) { const allocation = Math.min(remaining, Number(invoice.net_balance_minor)); if (allocation <= 0) break; await api.post(`/finance/payments/${payment.id}/allocations`, { invoice_id: invoice.id, amount_minor: allocation, allocation_key: `${paymentKey}:${invoice.id}` }); remaining -= allocation; }
    toast.success(remaining ? `Payment recorded; ${NGN(remaining)} remains as placement credit` : "Payment recorded and allocated"); setOpen(false); setSelected([]); setAmount(""); onRecorded();
  } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); } };
  return <Card className="p-5 border-[#E2E8F0] mb-5"><div className="flex items-center justify-between gap-3"><div><h2 className="font-display text-xl text-[#1B2C5A]">Manual payments</h2><p className="text-sm text-[#475569]">Creates a canonical placement payment, allocations and receipt.</p></div><Button onClick={() => setOpen((x) => !x)}>Record Payment</Button></div>{open && <div className="mt-5 border-t pt-5 space-y-4"><div className="space-y-2"><Label>Outstanding invoices</Label>{invoices.length ? invoices.map((x) => <label key={x.id} className="flex gap-2 items-center border rounded p-2 text-sm"><input type="checkbox" checked={selected.includes(x.id)} onChange={() => toggle(x.id)} /><span className="flex-1">{x.title || x.fee_item?.name || x.invoice_no}</span><strong>{NGN(x.net_balance_minor)}</strong></label>) : <p className="text-sm text-slate-500">No outstanding canonical invoices.</p>}</div><div className="grid md:grid-cols-4 gap-3"><div><Label>Actual amount received (₦)</Label><Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></div><div><Label>Method</Label><Select value={method} onValueChange={setMethod}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="pos">POS</SelectItem><SelectItem value="mobile">Other Manual</SelectItem></SelectContent></Select></div><div><Label>Transaction / receipt reference</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} /></div><div><Label>Payment date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div></div><Input placeholder="Optional note" value={note} onChange={(e) => setNote(e.target.value)} /><Button disabled={saving || !invoices.length} onClick={submit}>{saving ? "Recording…" : "Confirm Payment"}</Button></div>}</Card>;
};

const StatementView = ({ statement, placement }) => {
  const reconciliation = statement.reconciliation || {};
  const balanced = reconciliation.status === "balanced";
  const metrics = [
    ["Charged", statement.total_charged_minor, "charged"],
    ["Scholarship / Discount", statement.total_scholarship_minor, "scholarship"],
    ["Payments", statement.total_payments_received_minor, "payments"],
    ["Allocated", statement.total_allocated_minor, "allocated"],
    ["Available Credit", statement.available_credit_minor, "credit"],
    ["Outstanding Balance", statement.outstanding_balance_minor, "outstanding"],
  ];
  return (
    <div data-testid="finance-statement">
      <Card className="p-5 border-[#E2E8F0] mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline text-[#9DCD3F]">Selected Placement</div>
          <div className="font-display text-xl text-[#1B2C5A] mt-1" data-testid="finance-placement-title">
            {statement.placement?.program_name || placement?.program_name || "Program"}
          </div>
          <div className="text-sm text-[#475569] mt-1">
            {[statement.placement?.class_name || placement?.class_name, statement.placement?.session_name, statement.placement?.term_name]
              .filter(Boolean).join(" · ")}
          </div>
        </div>
        <div
          className={`inline-flex items-center gap-2 self-start sm:self-auto rounded-full border px-3 py-1.5 text-sm font-medium ${balanced
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-red-50 text-red-700 border-red-200"}`}
          data-testid="finance-reconciliation-status"
        >
          {balanced ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          Reconciliation: {balanced ? "Balanced" : "Mismatch"}
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        {metrics.map(([label, value, key]) => (
          <Card key={key} className={`p-5 border-[#E2E8F0] ${key === "outstanding" ? "border-l-4 border-l-[#9DCD3F]" : ""}`}>
            <div className="overline text-[#9DCD3F]">{label}</div>
            <div className="font-display text-2xl text-[#1B2C5A] mt-2" data-testid={`finance-${key}`}>{NGN(value)}</div>
          </Card>
        ))}
      </div>

      {!balanced && reconciliation.differences?.length > 0 && (
        <InlineError message={`${reconciliation.differences.length} reconciliation difference(s) require review.`} />
      )}

      <Card className="border-[#E2E8F0] overflow-hidden">
        <div className="p-5 border-b border-[#E2E8F0]">
          <h2 className="font-display text-xl text-[#1B2C5A]">Statement activity</h2>
          <p className="text-sm text-[#475569] mt-1">Read-only ledger events for this placement.</p>
        </div>
        {!statement.entries?.length ? <EmptyState message="No statement activity for this placement." /> : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FDFBF7]">
                <TableHead>Date</TableHead><TableHead>Activity</TableHead><TableHead>Details</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {statement.entries.map((entry) => (
                <TableRow key={entry.id} data-testid={`finance-entry-${entry.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-[#475569]">{new Date(entry.occurred_at).toLocaleDateString()}</TableCell>
                  <TableCell className="font-medium text-[#1B2C5A]">{ENTRY_LABELS[entry.entry_type] || entry.entry_type.replaceAll("_", " ")}</TableCell>
                  <TableCell className="text-[#475569]">{entry.description || entry.source?.event || "—"}</TableCell>
                  <TableCell className="text-right font-medium">{NGN(Math.abs(entry.receivable_delta_minor || entry.unallocated_payment_delta_minor || 0))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

const InlineError = ({ message, className = "" }) => (
  <div className={`rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2 ${className}`}>
    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {message}
  </div>
);

export default Fees;
